import { assertInInjectionContext, inject, Injector, runInInjectionContext, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { GenericFeatureFlagService } from '../util-feature-flag-service/feature-flag.abstract.service';

/**
 * Factory function to create a strongly-typed feature flag signal generator.
 *
 * This allows consumers to define their own feature flag key type once,
 * and use the returned function throughout their codebase without repeating the type.
 *
 * @template FeatureFlags - The union type of valid feature flag keys for your application.
 * @param useLive - If true, the signal will update reactively as the flag value changes (using getLiveFeature$).
 *               If false (default), the signal will emit the current value once (using getFeature$).
 * @returns A function that takes a feature flag key, a default value, and optionally an Injector,
 *          and returns a Signal of the feature flag value.
 *
 * @example
 * // Define your flag keys
 * type MyFlags = 'flagA' | 'flagB';
 * // Create a typed signal generator
 * const featureFlagSignal = createFeatureFlagSignal<MyFlags>();
 * // Use in your components/services
 * const flagSignal = featureFlagSignal('flagA', false);
 */
export function createFeatureFlagSignal<FeatureFlags>(useLive = false) {
  return function <T>(flag: FeatureFlags, defaultValue: T, injector?: Injector): Signal<T> {
    if (!injector) {
      assertInInjectionContext(createFeatureFlagSignal);
    }
    injector ??= inject(Injector);
    const result = runInInjectionContext(injector, () => {
      const service = inject(GenericFeatureFlagService);
      return useLive ? service.getLiveFeature$(flag, defaultValue) : service.getFeature$(flag, defaultValue);
    });
    return toSignal(result, { initialValue: defaultValue });
  };
}
